import api from '../api';

export interface SubscriptionResponse {
  id: number;
  company_id: number;
  status: 'ACTIVE' | 'SUSPENDED' | 'CANCELED' | 'EXPIRED';
  plan_id?: number;
  contracted_lives: number;
  started_at: string;
  expires_at?: string;
  plan?: {
    id: number;
    name: string;
    slug: string;
    features?: Array<{
      id: number;
      name: string;
      key: string;
      included: boolean;
      extraCost?: boolean;
    }>;
  };
  charge_clinical_exams?: boolean; // Flag que determina se exames clínicos serão cobrados
  payment?: {
    success: boolean;
    transaction_id?: string;
    message: string;
    payment_data?: {
      // PIX fields
      qr_code?: string;
      qr_code_text?: string;
      expires_at?: string;
      manual?: boolean;
      pix_cnpj?: string;
      // Card fields
      cardToken?: string;
      responseCode?: string;
      authorizationCode?: string;
      // Common
      transaction_id?: string;
    };
  };
  planSnapshot?: any; // Snapshot do plano no momento da assinatura
  access_token?: string; // Token retornado na criação (fluxo unauthenticated)
  empresaId?: number; // ID da empresa criada/encontrada
  solicitanteId?: number; // ID do solicitante
}

export interface CreateSubscriptionDto {
  planSlug: string;
  contractedLives: number;
  bracketId?: number;
  companyData: {
    razao_social: string;
    nome_fantasia?: string;
    cnpj?: string;
    cpf?: string;
    telefone: string;
    email: string;
    ocupation: 'PJ' | 'PF';
    endereco?: {
      logradouro: string;
      numero: string;
      complemento?: string;
      bairro: string;
      cidade: string;
      estado: string;
      cep: string;
    };
  };
  paymentData?: {
    paymentMethod: 'credit_card' | 'pix';
    cardNumber?: string;
    cardName?: string;
    cardExpiry?: string;
    cardCvv?: string;
    cardCpf?: string;
  };
}

/**
 * Repository para operações relacionadas a assinaturas
 */
export class SubscriptionsRepository {
  /**
   * Verifica se a empresa tem uma assinatura ativa
   * Requer token válido
   */
  async checkActiveSubscription(): Promise<SubscriptionResponse | null> {
    try {
      const response = await api.get<SubscriptionResponse>(
        'subscriptions/active'
      );
      return response.data;
    } catch (error: any) {
      // Se retornar 404, significa que não há assinatura ativa ou endpoint não existe
      // Se retornar 401/403, significa que não há token válido
      // Em ambos os casos, retornamos null silenciosamente
      if (error.response?.status === 404 ||
        error.response?.status === 401 ||
        error.response?.status === 403 ||
        error.response?.status === 500) {
        return null;
      }
      // Para outros erros, também retornamos null para não quebrar o fluxo
      // O endpoint pode não estar implementado ainda
      return null;
    }
  }

  /**
   * Verifica se existe assinatura ativa por CNPJ
   * Não requer token (endpoint público)
   */
  async checkActiveSubscriptionByCnpj(cnpj: string): Promise<SubscriptionResponse | null> {
    try {
      const response = await api.get<SubscriptionResponse>(
        `subscriptions/check-by-cnpj?cnpj=${encodeURIComponent(cnpj)}`
      );
      return response.data;
    } catch (error: any) {
      // Se retornar 404, significa que não há assinatura ativa
      if (error.response?.status === 404) {
        return null;
      }
      // Para outros erros, retornamos null silenciosamente
      return null;
    }
  }

  /**
   * Criar nova assinatura
   * Requer token válido
   */
  async create(data: CreateSubscriptionDto): Promise<SubscriptionResponse> {
    const response = await api.post<SubscriptionResponse>(
      'subscriptions',
      data
    );
    return response.data;
  }
}

let subscriptionsRepositoryInstance: SubscriptionsRepository | null = null;

export function getSubscriptionsRepository(): SubscriptionsRepository {
  if (!subscriptionsRepositoryInstance) {
    subscriptionsRepositoryInstance = new SubscriptionsRepository();
  }
  return subscriptionsRepositoryInstance;
}

